// Golf-specific business logic: playing handicap, snake draft, scoring.
import type { Course, Player, Round, Trip, LeaderboardEntry } from "./types";

// ── Handicap Index formatting & parsing ──────────────────────────────

/** Display a stored whs_index. Negative internal value → "+X.X" (plus handicap). */
export function formatHandicapIndex(val: number | null | undefined): string {
  if (val == null || isNaN(val as number)) return '—';
  if (val < 0) return `+${Math.abs(val).toFixed(1)}`;
  return (val as number).toFixed(1);
}

/** Display WHS for a player. PH-mode players show "–" instead of "0.0". */
export function displayWHS(player: Pick<Player, 'handicap_mode' | 'whs_index'> | null | undefined): string {
  if (!player) return '—';
  if (player.handicap_mode === 'ph') return '–';
  return formatHandicapIndex(player.whs_index);
}

/** Parse user-typed handicap string into internal whs_index number.
 *  "+3" → -3, "-3" → -3, "3" → 3 (bare positive = regular handicap). */
export function parseHandicapInput(raw: string): number {
  const trimmed = raw.trim();
  if (!trimmed) return 0;
  if (trimmed.startsWith('+')) {
    const v = parseFloat(trimmed.slice(1));
    return isNaN(v) ? 0 : -v;
  }
  const v = parseFloat(trimmed);
  return isNaN(v) ? 0 : v;
}

/** Format stored whs_index for pre-filling an input field. Negative → "+X". */
export function formatHandicapForInput(val: number | null | undefined): string {
  if (val == null || isNaN(val as number)) return '';
  if (val < 0) return `+${Math.abs(val)}`;
  return String(val);
}

/** Display a Playing Handicap (integer). Negative → "+X" (plus handicap). */
export function formatPlayingHandicap(val: number | null | undefined): string {
  if (val == null || isNaN(val as number)) return '—';
  if (val < 0) return `+${Math.abs(val)}`;
  return String(val);
}

/** Get the effective Playing Handicap for a player.
 *  Returns manual_ph directly when handicap_mode === "ph", else computes via WHS formula. */
export function getEffectivePH(player: Player, course: Course): number {
  if (player.handicap_mode === 'ph' && player.manual_ph != null) {
    return player.manual_ph;
  }
  return playingHandicap(player, course);
}

// Apply handicap allowance percentage to a playing handicap.
// Half always rounds up on magnitude. Sign is preserved (plus-handicaps stay plus).
export function allowedPH(effectivePH: number, pct: number): number {
  if (pct === 100) return effectivePH;
  const sign = effectivePH < 0 ? -1 : 1;
  const mag = Math.abs(effectivePH);
  const scaled = mag * (pct / 100);
  const rounded = Math.floor(scaled + 0.5);
  return sign * rounded;
}

// Get the allowed PH for a player given a course and round's allowance setting.
// Legacy rounds (no handicap_allowance_pct) default to 100% (no change).
export function getAllowedPH(player: Player, course: Course, round?: Round | null): number {
  const ph = getEffectivePH(player, course);
  const pct = (round && Number.isFinite(round.handicap_allowance_pct)) ? round.handicap_allowance_pct! : 100;
  return allowedPH(ph, pct);
}


// ── Division helpers ─────────────────────────────────────────────────

/** Assign a player to a division based on their handicap.
 *  Uses whs_index for WHS mode, manual_ph for PH mode. No allowance applied.
 *  Returns division id or null if no match. */
export function assignDivision(
  player: { handicap_mode?: string; whs_index?: number; manual_ph?: number | null },
  divisions: { id: string; min_handicap: number; max_handicap: number }[] | null | undefined,
): string | null {
  if (!divisions || divisions.length === 0) return null;
  const h = player.handicap_mode === 'ph'
    ? (player.manual_ph ?? null)
    : (player.whs_index ?? null);
  if (h == null) return null;
  for (const d of divisions) {
    if (h >= d.min_handicap && h <= d.max_handicap) return d.id;
  }
  return null;
}

/** Format a handicap for division display: negative values shown as +N */
export function formatDivisionHandicap(v: number): string {
  if (v <= -999) return 'unbounded low';
  if (v >= 999) return 'unbounded high';
  if (v < 0) return `+${Math.abs(v)}`;
  return String(v);
}


// ── System 36 Handicap helpers ───────────────────────────────────────

/** S36 points for a single hole: 2 if gross ≤ par, 1 if bogey, 0 if double+ or unplayed. */
export function s36PointsForHole(grossStrokes: number, par: number): number {
  if (!grossStrokes || grossStrokes <= 0) return 0;
  if (grossStrokes <= par) return 2;
  if (grossStrokes === par + 1) return 1;
  return 0;
}

/** Sum S36 points over 18 holes. */
export function s36PointsTotal(playerHoleStrokes: number[], pars: number[]): number {
  let total = 0;
  for (let h = 0; h < 18; h++) {
    total += s36PointsForHole(playerHoleStrokes[h] ?? 0, pars[h] ?? 4);
  }
  return total;
}

/** S36 handicap = 36 − total points. Returns null if any hole is missing/0 (round not complete).
 *  If maxCap is set, clamp to min(handicap, maxCap). */
export function s36Handicap(
  playerHoleStrokes: number[],
  pars: number[],
  maxCap: number | null | undefined,
): number | null {
  for (let h = 0; h < 18; h++) {
    if (!playerHoleStrokes[h] || playerHoleStrokes[h] <= 0) return null;
  }
  const points = s36PointsTotal(playerHoleStrokes, pars);
  let hcap = 36 - points;
  if (maxCap != null && hcap > maxCap) hcap = maxCap;
  return hcap;
}

/** Determine the effective playing handicap for a given basis.
 *  @param basis "entered" | "system36" | "lower_of" (absent/undefined = "entered")
 *  @param allowedEnteredPH entered PH after allowance percentage
 *  @param s36Hcap S36 handicap (null if round incomplete) */
export function effectivePHForBasis(
  basis: string | undefined,
  allowedEnteredPH: number,
  s36Hcap: number | null,
): { ph: number | null; source: "entered_at_allowance" | "s36" | null; s36_handicap: number | null } {
  const b = basis || "entered";
  if (b === "entered") {
    return { ph: allowedEnteredPH, source: "entered_at_allowance", s36_handicap: null };
  }
  if (b === "system36") {
    return { ph: s36Hcap, source: s36Hcap != null ? "s36" : null, s36_handicap: s36Hcap };
  }
  // lower_of
  if (s36Hcap == null) {
    return { ph: null, source: null, s36_handicap: null };
  }
  // Compare as stored: plus handicaps are negative numbers, so -4 < 10
  if (allowedEnteredPH <= s36Hcap) {
    return { ph: allowedEnteredPH, source: "entered_at_allowance", s36_handicap: s36Hcap };
  }
  return { ph: s36Hcap, source: "s36", s36_handicap: s36Hcap };
}

// WHS Playing Handicap = round(WHS × (slope/113) + (course_rating − par))
export function playingHandicap(player: Player, course: Course): number {
  const raw =
    player.whs_index * (course.slope_rating / 113) +
    (course.course_rating - course.par);
  return Math.round(raw);
}

// Default hole-par distribution for par 72: 4 par-3s + 10 par-4s + 4 par-5s.
// Alternating layout so each nine sums to 36 (verified 36+36=72).
const DEFAULT_PARS_72 = [4, 3, 4, 5, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 3, 5, 4, 4];

export function defaultHolePars(par: number): number[] {
  const baseSum = DEFAULT_PARS_72.reduce((a, b) => a + b, 0); // 72
  const delta = par - baseSum;
  if (delta === 0) return DEFAULT_PARS_72.slice();
  const out = DEFAULT_PARS_72.slice();
  let remaining = delta;
  // Adjust by nudging par-4s to par-5 (+1) or par-4 to par-3 (-1) — leave
  // the first hole alone so it stays a par 4 for consistency.
  for (let i = out.length - 1; i >= 1 && remaining > 0; i--) {
    if (out[i] === 4) { out[i] = 5; remaining--; }
  }
  for (let i = out.length - 1; i >= 1 && remaining < 0; i--) {
    if (out[i] === 4) { out[i] = 3; remaining++; }
  }
  return out;
}

// Get the effective hole pars for a round: prefer round.hole_pars if set,
// then course.hole_pars, then a default distribution derived from course.par.
export function effectiveHolePars(round: Round): number[] {
  if (round.hole_pars && round.hole_pars.length === 18) return round.hole_pars;
  if (round.course.hole_pars && round.course.hole_pars.length === 18) return round.course.hole_pars;
  return defaultHolePars(round.course.par);
}

// Default stroke-index (SI) layout — alternating between front & back 9 so
// odd holes across the round scale evenly. 1 = hardest hole.
const DEFAULT_SI = [7, 13, 3, 17, 5, 9, 15, 1, 11, 8, 4, 18, 12, 6, 2, 16, 14, 10];

export function effectiveStrokeIndexes(round: Round): number[] {
  if (round.stroke_indexes && round.stroke_indexes.length === 18) return round.stroke_indexes;
  if (round.course.stroke_indexes && round.course.stroke_indexes.length === 18) return round.course.stroke_indexes;
  return DEFAULT_SI;
}

// Shots RECEIVED on a given hole based on playing_hc and stroke_index (1..18).
// Handles + handicaps by giving BACK shots on the easiest holes.
export function shotsOnHole(playingHc: number, si: number): number {
  if (playingHc >= 0) {
    const base = Math.floor(playingHc / 18);
    const rem = playingHc % 18;
    return base + (si <= rem ? 1 : 0);
  }
  const p = -playingHc;
  // Give back a shot on the p easiest holes (SI 19-p .. 18).
  return -(si >= 19 - p ? 1 : 0);
}

// Stableford points for a single hole given gross strokes, par, playing HC, SI.
// Returns null when gross is missing (< 1). Otherwise: max(0, 2 + par - net).
export function stablefordPoints(
  gross: number | undefined | null,
  par: number,
  playingHc: number,
  si: number,
): number | null {
  if (gross == null || !Number.isFinite(gross) || gross < 1) return null;
  const shots = shotsOnHole(playingHc, si);
  const net = gross - shots;
  return Math.max(0, 2 + par - net);
}

// -------- Team draw --------
//
// Old snake-draft would always pair the lowest & highest handicappers together
// across rounds. We now do a *tiered* draw and score candidates against a
// running "already-paired" set from previous rounds, picking the best (fewest
// repeat partners) while keeping teams balanced by playing handicap.

function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

// Splits players (already sorted low→high by "order key") into `teamSize`
// tiers of ~equal size, shuffles inside each tier, and assigns one from each
// tier to each team. Guarantees every team gets one player from each tier so
// balance is maintained, but many different valid line-ups can be produced.
function tieredDraw(
  sortedIds: string[],
  teamSize: number,
): string[][] {
  const numTeams = Math.max(1, Math.ceil(sortedIds.length / teamSize));
  const teams: string[][] = Array.from({ length: numTeams }, () => []);

  // Slice into `teamSize` tiers of size `numTeams` (last tier may be shorter)
  const tiers: string[][] = [];
  for (let t = 0; t < teamSize; t++) {
    tiers.push(sortedIds.slice(t * numTeams, (t + 1) * numTeams));
  }

  // Shuffle within each tier so tier -> team assignment is randomised.
  for (const tier of tiers) shuffleInPlace(tier);

  for (const tier of tiers) {
    for (let i = 0; i < tier.length; i++) {
      teams[i].push(tier[i]);
    }
  }
  return teams;
}

function pairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

// Count how many "pair repeats" a candidate draw introduces vs previous rounds.
function repeatPairsScore(teams: string[][], previousPairs: Set<string>): number {
  let n = 0;
  for (const t of teams) {
    for (let i = 0; i < t.length; i++) {
      for (let j = i + 1; j < t.length; j++) {
        if (previousPairs.has(pairKey(t[i], t[j]))) n++;
      }
    }
  }
  return n;
}

// Balance penalty: variance of team-handicap sums (lower = more balanced)
function balanceScore(teams: string[][], order: (id: string) => number): number {
  const sums = teams.map((t) => t.reduce((a, id) => a + order(id), 0));
  const mean = sums.reduce((a, b) => a + b, 0) / (sums.length || 1);
  return sums.reduce((acc, s) => acc + (s - mean) * (s - mean), 0);
}

// Tee-off position fairness: penalise placing players in the first or last
// group when they were already in that position in prior rounds.
// Recent rounds weighted higher (most recent = 3, one before = 2, earlier = 1).
// Returns 0 for single-round trips or ≤2 groups (no middle to push to).
function positionPenalty(teams: string[][], previousTeams: string[][][]): number {
  if (teams.length <= 2 || previousTeams.length === 0) return 0;
  let penalty = 0;
  const firstGroup = new Set(teams[0]);
  const lastGroup = new Set(teams[teams.length - 1]);
  for (let r = previousTeams.length - 1; r >= 0; r--) {
    const w = Math.min(3, previousTeams.length - r);
    const prevFirst = previousTeams[r][0] || [];
    const prevLast = previousTeams[r][previousTeams[r].length - 1] || [];
    for (const id of prevFirst) if (firstGroup.has(id)) penalty += w;
    for (const id of prevLast) if (lastGroup.has(id)) penalty += w;
  }
  return penalty;
}

export function generateRoundDraw(
  playerIds: string[],
  order: (id: string) => number,
  teamSize: number,
  previousTeams: string[][][], // teams per previous round (locked or not)
  attempts = 400,
): string[][] {
  const sorted = [...playerIds].sort((a, b) => order(a) - order(b));

  // Tiered-random with previous-partner avoidance for balance.
  const previousPairs = new Set<string>();
  for (const roundTeams of previousTeams) {
    for (const team of roundTeams) {
      for (let i = 0; i < team.length; i++) {
        for (let j = i + 1; j < team.length; j++) {
          previousPairs.add(pairKey(team[i], team[j]));
        }
      }
    }
  }

  // When prior rounds exist, the pair+position constraint space grows tight
  // (e.g. 12 players/3 groups has only 1296 unique tiered configs). Use more
  // attempts to cover the space adequately.
  const effectiveAttempts = previousTeams.length > 0
    ? Math.max(attempts, 1200)
    : attempts;

  let best: string[][] | null = null;
  let bestScore = Infinity;
  for (let a = 0; a < effectiveAttempts; a++) {
    const teams = tieredDraw(sorted, teamSize);
    const repeats = repeatPairsScore(teams, previousPairs);
    const bal = balanceScore(teams, order);
    const posn = positionPenalty(teams, previousTeams);
    const score = repeats * 1000 + posn * 200 + bal;
    if (score < bestScore) {
      bestScore = score;
      best = teams;
      if (repeats === 0 && posn === 0 && bal < 4) break;
    }
  }
  return best ?? tieredDraw(sorted, teamSize);
}

// -------- Scoring --------

// Individual points table: 1st=50, 2nd=20, 3rd=15, 4th=10, 5th=5.
// (Deprecated — kept for reference; the current app scores individuals purely
// by Stableford total across rounds, no league-points overlay.)
const INDIVIDUAL_POINTS = [50, 20, 15, 10, 5];

// Given a map player_id -> stableford, return player_id -> league points
// (tied players share combined slot points equally).
export function individualRoundPoints(
  scores: Record<string, number>,
): Record<string, number> {
  const entries = Object.entries(scores).filter(([, v]) => Number.isFinite(v));
  if (entries.length === 0) return {};
  entries.sort((a, b) => b[1] - a[1]);

  const result: Record<string, number> = {};
  let i = 0;
  while (i < entries.length) {
    const [, s] = entries[i];
    let j = i;
    while (j < entries.length && entries[j][1] === s) j++;
    let combined = 0;
    for (let k = i; k < j; k++) combined += INDIVIDUAL_POINTS[k] ?? 0;
    const share = combined / (j - i);
    for (let k = i; k < j; k++) result[entries[k][0]] = share;
    i = j;
  }
  for (const [pid] of entries) if (!(pid in result)) result[pid] = 0;
  return result;
}

// Sum a player's per-hole Stableford points into a round total.
export function totalFromHoles(holes: number[] | undefined): number {
  if (!holes) return 0;
  return holes.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);
}

// Team round scoring — pure Stableford total per team, using the format rule.
// Default: all count on par 3, top-N on par 4/5 where N = ceil(size/2).
// When round.team_counting is set, the per-par counts are configurable.

/** Legacy helper — kept for backward compatibility in teamContribution.ts imports.
 *  Returns ceil(teamSize/2) — the default count on par 4/5. */
export function countingScoresOnLongHoles(teamSize: number): number {
  if (teamSize <= 1) return 1;
  return Math.ceil(teamSize / 2);
}

/** Number of individual scores that count on a hole, respecting the configurable
 *  round.team_counting rule. Falls back to the hard-coded defaults when absent. */
export function countForPar(round: Round | undefined, par: number, teamSize: number): number {
  const rule = round?.team_counting;
  const key = par <= 3 ? 'par3' : par === 4 ? 'par4' : 'par5';
  const val = rule?.[key];
  if (val === 'all' || val === undefined) {
    return par <= 3 ? teamSize : countingScoresOnLongHoles(teamSize);
  }
  return Math.min(val as number, teamSize);
}

export function teamRoundPoints(
  teams: string[][],
  scores: Record<string, number>,
  hole_scores?: Record<string, number[]>,
  hole_pars?: number[],
  hole_nr?: Record<string, number[]>,
  round?: Round,
): { team_index: number; player_ids: string[]; stableford_total: number; team_points: number; rank: number; cb?: boolean }[] {
  const hasHoles = hole_scores && hole_pars && hole_pars.length === 18;

  const teamsWithScore = teams
    .map((players, idx) => {
      let total = 0;
      let hasAnyScore = false;
      if (hasHoles) {
        for (let h = 0; h < 18; h++) {
          const par = hole_pars![h];
          const countN = countForPar(round, par, players.length);
          const pts = players
            .map((pid) => {
              if (hole_nr?.[pid]?.includes(h)) return undefined;
              return hole_scores![pid]?.[h];
            })
            .filter((v): v is number => Number.isFinite(v) && v! >= 0);
          if (pts.length === 0) continue;
          hasAnyScore = true;
          pts.sort((a, b) => b - a);
          const take = Math.min(countN, pts.length);
          for (let k = 0; k < take; k++) total += pts[k] ?? 0;
        }
      } else {
        // Fallback when hole_scores or hole_pars missing: iterate per-player
        // hole_scores with NR filter instead of reading stale scores[pid] aggregate
        for (const pid of players) {
          const pHoleScores = hole_scores?.[pid] ?? [];
          const pNr = hole_nr?.[pid] ?? [];
          for (let h = 0; h < pHoleScores.length; h++) {
            if (pNr.includes(h)) continue;
            const v = pHoleScores[h];
            if (v != null && v >= 0) { total += v; hasAnyScore = true; }
          }
          // If no hole_scores at all, fall back to aggregate (legacy)
          if (pHoleScores.length === 0 && scores[pid] != null) {
            total += scores[pid]; hasAnyScore = true;
          }
        }
      }
      const cbTuple = hasHoles
        ? teamCountbackTuple(players, hole_scores, hole_pars, hole_nr, round)
        : ([0, 0, 0, 0, 0] as CountbackTuple);
      return { team_index: idx, player_ids: players, stableford_total: total, hasAnyScore, cbTuple };
    })
    .filter((t) => t.hasAnyScore);

  // Sort by SF total then by countback tuple.
  teamsWithScore.sort((a, b) => {
    if (b.stableford_total !== a.stableford_total) return b.stableford_total - a.stableford_total;
    return compareCountback(a.cbTuple, b.cbTuple);
  });

  const results: { team_index: number; player_ids: string[]; stableford_total: number; team_points: number; rank: number; cb?: boolean }[] = [];
  let i = 0;
  while (i < teamsWithScore.length) {
    let j = i;
    while (
      j < teamsWithScore.length &&
      teamsWithScore[j].stableford_total === teamsWithScore[i].stableford_total &&
      compareCountback(teamsWithScore[j].cbTuple, teamsWithScore[i].cbTuple) === 0
    ) {
      j++;
    }
    const rank = i + 1;
    for (let k = i; k < j; k++) {
      results.push({
        team_index: teamsWithScore[k].team_index,
        player_ids: teamsWithScore[k].player_ids,
        stableford_total: teamsWithScore[k].stableford_total,
        team_points: 0, // deprecated
        rank,
      });
    }
    i = j;
  }
  // Flag CB on any team that shared a total with a differently-ranked peer.
  const byTotal = new Map<number, typeof results>();
  for (const r of results) {
    const arr = byTotal.get(r.stableford_total) ?? [];
    arr.push(r);
    byTotal.set(r.stableford_total, arr);
  }
  for (const arr of byTotal.values()) {
    if (arr.length <= 1) continue;
    const ranks = new Set(arr.map((r) => r.rank));
    if (ranks.size > 1) for (const r of arr) r.cb = true;
  }
  return results;
}

// -------- League --------

// -------- Countback (WHS / R&A standard for Stableford tiebreak) --------
//
// When two players or teams tie on total Stableford points after 18 holes,
// the official tiebreak sequence is:
//   1. best Stableford over back 9 (holes 10–18)
//   2. best over back 6 (holes 13–18)
//   3. best over back 3 (holes 16–18)
//   4. best on hole 18 alone
//   5. best on hole 17 alone
// If still tied, positions are shared and league points split equally.

export type CountbackTuple = [number, number, number, number, number];

export const COUNTBACK_SLICES: number[][] = [
  [9, 10, 11, 12, 13, 14, 15, 16, 17], // back 9
  [12, 13, 14, 15, 16, 17],            // back 6
  [15, 16, 17],                         // back 3
  [17],                                 // hole 18
  [16],                                 // hole 17
];

export function playerHoleSum(round: Round, playerId: string, holes: number[]): number {
  const pts = round.hole_scores?.[playerId] ?? [];
  const nrHoles = round.hole_nr?.[playerId] ?? [];
  let total = 0;
  for (const h of holes) {
    if (nrHoles.includes(h)) continue;
    const v = pts[h];
    if (v != null && v >= 0) total += v;
  }
  return total;
}

/** Individual countback tuple aggregated across every round in the trip. */
export function individualCountbackTuple(trip: Trip, playerId: string): CountbackTuple {
  const out: number[] = [0, 0, 0, 0, 0];
  for (const round of trip.rounds) {
    for (let s = 0; s < COUNTBACK_SLICES.length; s++) {
      out[s] += playerHoleSum(round, playerId, COUNTBACK_SLICES[s]);
    }
  }
  return out as CountbackTuple;
}

/** Team Stableford points across an arbitrary set of hole indexes for a single
 *  round. Applies the "top-N on par 4/5, all on par 3" rule. */
export function teamHoleSum(
  playerIds: string[],
  hole_scores: Record<string, number[]> | undefined,
  hole_pars: number[] | undefined,
  holes: number[],
  hole_nr?: Record<string, number[]>,
  round?: Round,
): number {
  if (!hole_scores || !hole_pars || hole_pars.length !== 18) return 0;
  let total = 0;
  for (const h of holes) {
    const par = hole_pars[h] ?? 4;
    const countN = countForPar(round, par, playerIds.length);
    const pts = playerIds
      .map((pid) => {
        if (hole_nr?.[pid]?.includes(h)) return undefined;
        return hole_scores[pid]?.[h];
      })
      .filter((v): v is number => Number.isFinite(v) && (v as number) >= 0);
    if (pts.length === 0) continue;
    pts.sort((a, b) => b - a);
    const take = Math.min(countN, pts.length);
    for (let k = 0; k < take; k++) total += pts[k] ?? 0;
  }
  return total;
}

/** Team countback tuple within a single round. */
export function teamCountbackTuple(
  playerIds: string[],
  hole_scores: Record<string, number[]> | undefined,
  hole_pars: number[] | undefined,
  hole_nr?: Record<string, number[]>,
  round?: Round,
): CountbackTuple {
  const out: number[] = [];
  for (const slice of COUNTBACK_SLICES) {
    out.push(teamHoleSum(playerIds, hole_scores, hole_pars, slice, hole_nr, round));
  }
  return out as CountbackTuple;
}

export function compareCountback(a: CountbackTuple, b: CountbackTuple): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return b[i] - a[i]; // higher is better (Stableford)
  }
  return 0;
}

/** Compare NSP countback tuples (lower net strokes = better). */
export function compareCountbackNSP(a: CountbackTuple, b: CountbackTuple): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i]; // lower is better
  }
  return 0;
}

/** Build an NSP countback tuple from per-hole net stroke array. */
export function nspCountbackFromNets(holeNets: number[]): CountbackTuple {
  return COUNTBACK_SLICES.map(slice => {
    let sum = 0;
    for (const h of slice) {
      const v = holeNets[h];
      if (v != null) sum += v;
    }
    return sum;
  }) as CountbackTuple;
}

// -------- Individual leaderboard with countback --------

// Individual league: pure Stableford total across rounds (no league-points table).
export function computeIndividualLeaderboard(trip: Trip): LeaderboardEntry[] {
  const map = new Map<string, LeaderboardEntry>();
  for (const p of trip.players) {
    map.set(p.id, { player_id: p.id, name: p.name, total: 0, per_round: {} });
  }
  for (const round of trip.rounds) {
    const frozen = isRoundFrozen(round);
    const throughPos = frozen ? revealHoleForRound(round) : 18; // 1-indexed playing-position cap
    for (const p of trip.players) {
      const entry = map.get(p.id);
      if (!entry) continue;
      let sf = 0;
      if (frozen) {
        // Freeze-aware: sum only visible holes per group's playing sequence
        const gIdx = groupIndexForPlayer(round, p.id);
        const pts = round.hole_scores?.[p.id] ?? [];
        const nrHoles = round.hole_nr?.[p.id] ?? [];
        for (let h = 0; h < 18; h++) {
          if (nrHoles.includes(h)) continue;
          if (holeInPlayingRange(round, gIdx, h, throughPos)) {
            const v = pts[h];
            if (v != null && v >= 0) sf += v;
          }
        }
      } else {
        sf = stablefordThroughHole(round, p.id, 17);
      }
      if (sf > 0) {
        entry.per_round[round.id] = sf;
        entry.total += sf;
      }
    }
  }
  // Sort by total (desc), then by countback tuple (desc). Attach rank + a
  // `cb` flag on any entry that was separated from a tied group purely by
  // countback (helpful for the UI to render a "CB" chip).
  const entries = Array.from(map.values()).map((e) => ({
    ...e,
    _cb: individualCountbackTuple(trip, e.player_id),
  }));
  entries.sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return compareCountback(a._cb, b._cb);
  });
  // Assign shared ranks for players tied on both total AND full countback.
  let i = 0;
  while (i < entries.length) {
    let j = i;
    while (
      j < entries.length &&
      entries[j].total === entries[i].total &&
      compareCountback(entries[j]._cb, entries[i]._cb) === 0
    ) {
      j++;
    }
    const rank = i + 1;
    for (let k = i; k < j; k++) entries[k].rank = rank;
    i = j;
  }
  // Any player who tied on total but ended up in a different rank than
  // another same-total peer was separated by countback — flag every member
  // of such a total-group so the UI can render a "CB" chip.
  const byTotal = new Map<number, typeof entries>();
  for (const e of entries) {
    const arr = byTotal.get(e.total) ?? [];
    arr.push(e);
    byTotal.set(e.total, arr);
  }
  for (const arr of byTotal.values()) {
    if (arr.length <= 1) continue;
    const ranks = new Set(arr.map((e) => e.rank ?? 0));
    if (ranks.size > 1) {
      for (const e of arr) e.cb = true;
    }
  }
  return entries.map(({ _cb, ...rest }) => rest);
}

// -------- Net Stroke Play leaderboard (per-round) --------

export interface NSPLeaderboardEntry {
  player_id: string;
  name: string;
  gross: number;
  handicap: number;
  net: number;
  thru: number;
  par_played: number;
  rank?: number;
  cb?: boolean;
  s36_handicap?: number | null;
  basis_source?: string | null;
  s36_net_pending?: boolean;
  allowed_entered_ph?: number;
}

/** Per-round NSP leaderboard: per-hole net (gross − strokes_received), sorted ascending.
 *  courseForPlayer: optional resolver for per-player tee overrides.
 *  siForPlayer: optional resolver for per-player stroke indexes. */
export function computeRoundLeaderboardNSP(
  trip: Trip,
  round: Round,
  courseForPlayer?: (round: Round, playerId: string) => Course,
  siForPlayer?: (round: Round, playerId: string) => number[],
): NSPLeaderboardEntry[] {
  const entries: NSPLeaderboardEntry[] = [];
  const defaultSI = effectiveStrokeIndexes(round);
  const hp = effectiveHolePars(round);
  const basis = round.handicap_basis;
  const s36Max = round.s36_max_handicap ?? null;
  const isS36Basis = basis === 'system36' || basis === 'lower_of';
  for (const p of trip.players) {
    const strokes = round.hole_strokes?.[p.id] ?? [];
    const course = courseForPlayer ? courseForPlayer(round, p.id) : round.course;
    const enteredPH = getAllowedPH(p, course, round);
    const si = siForPlayer ? siForPlayer(round, p.id) : defaultSI;
    const s36Hcap = isS36Basis ? s36Handicap(strokes, hp, s36Max) : null;
    const { ph: effPH, source, s36_handicap } = effectivePHForBasis(basis, enteredPH, s36Hcap);
    const usePH = effPH ?? 0;
    const pending = isS36Basis && effPH == null;
    let net = 0;
    let gross = 0;
    let thru = 0;
    let par_played = 0;
    for (let h = 0; h < 18; h++) {
      const s = strokes[h];
      if (s != null && s > 0) {
        const received = shotsOnHole(usePH, si[h]);
        net += s - received;
        gross += s;
        par_played += hp[h] ?? 0;
        thru++;
      }
    }
    entries.push({
      player_id: p.id, name: p.name, gross, handicap: usePH, net, thru, par_played,
      ...(isS36Basis ? { s36_handicap: s36_handicap, basis_source: source, s36_net_pending: pending, allowed_entered_ph: enteredPH } : {}),
    });
  }
  // Compute per-player NSP countback tuples for tiebreaking
  const cbMap = new Map<string, CountbackTuple>();
  for (const e of entries) {
    if (e.s36_net_pending) continue;
    const p = trip.players.find(pl => pl.id === e.player_id);
    if (!p) continue;
    const strokes = round.hole_strokes?.[p.id] ?? [];
    const si = siForPlayer ? siForPlayer(round, p.id) : defaultSI;
    const holeNets: number[] = [];
    for (let h = 0; h < 18; h++) {
      const s = strokes[h];
      holeNets.push(s != null && s > 0 ? s - shotsOnHole(e.handicap, si[h]) : 0);
    }
    cbMap.set(p.id, nspCountbackFromNets(holeNets));
  }
  // Sort by TO PAR ascending (net - par_played), unplayed last, S36-pending after complete
  entries.sort((a, b) => {
    if (a.s36_net_pending && !b.s36_net_pending) return 1;
    if (!a.s36_net_pending && b.s36_net_pending) return -1;
    const aUnplayed = a.par_played === 0;
    const bUnplayed = b.par_played === 0;
    if (aUnplayed && bUnplayed) return 0;
    if (aUnplayed) return 1;
    if (bUnplayed) return -1;
    const aTP = a.net - a.par_played;
    const bTP = b.net - b.par_played;
    if (aTP !== bTP) return aTP - bTP;
    return compareCountbackNSP(
      cbMap.get(a.player_id) ?? [0,0,0,0,0] as CountbackTuple,
      cbMap.get(b.player_id) ?? [0,0,0,0,0] as CountbackTuple,
    );
  });
  // Assign ranks + cb flag (by TO PAR)
  let i = 0;
  while (i < entries.length) {
    if (entries[i].par_played === 0 || entries[i].s36_net_pending) { entries[i].rank = i + 1; i++; continue; }
    let j = i;
    const iTP = entries[i].net - entries[i].par_played;
    while (j < entries.length && entries[j].par_played > 0 && !entries[j].s36_net_pending && (entries[j].net - entries[j].par_played) === iTP) j++;
    for (let k = i; k < j; k++) entries[k].rank = i + 1;
    if (j - i >= 2) {
      const cbFirst = cbMap.get(entries[i].player_id);
      const cbLast = cbMap.get(entries[j - 1].player_id);
      if (cbFirst && cbLast && compareCountbackNSP(cbFirst, cbLast) !== 0) {
        for (let k = i; k < j; k++) entries[k].cb = true;
      }
    }
    i = j;
  }
  return entries;
}

/** Per-round NSP leaderboard frozen at a specific playing position.
 *  throughHole: 0-based position cap — for standard rounds sums holes 0..throughHole;
 *  for multi-start rounds sums holes whose playing-position ≤ throughHole+1. */
export function computeRoundLeaderboardNSPThrough(
  trip: Trip,
  round: Round,
  throughHole: number,
  courseForPlayer?: (round: Round, playerId: string) => Course,
  siForPlayer?: (round: Round, playerId: string) => number[],
): NSPLeaderboardEntry[] {
  const entries: NSPLeaderboardEntry[] = [];
  const defaultSI = effectiveStrokeIndexes(round);
  const throughPos = throughHole + 1;
  const hp = effectiveHolePars(round);
  const basis = round.handicap_basis;
  const s36Max = round.s36_max_handicap ?? null;
  const isS36Basis = basis === 'system36' || basis === 'lower_of';
  for (const p of trip.players) {
    const strokes = round.hole_strokes?.[p.id] ?? [];
    const course = courseForPlayer ? courseForPlayer(round, p.id) : round.course;
    const enteredPH = getAllowedPH(p, course, round);
    const si = siForPlayer ? siForPlayer(round, p.id) : defaultSI;
    const gIdx = groupIndexForPlayer(round, p.id);
    const s36Hcap = isS36Basis ? s36Handicap(strokes, hp, s36Max) : null;
    const { ph: effPH, source, s36_handicap } = effectivePHForBasis(basis, enteredPH, s36Hcap);
    const usePH = effPH ?? 0;
    const pending = isS36Basis && effPH == null;
    let gross = 0;
    let net = 0;
    let thru = 0;
    let par_played = 0;
    for (let h = 0; h < 18; h++) {
      if (!holeInPlayingRange(round, gIdx, h, throughPos)) continue;
      const s = strokes[h];
      if (s != null && s > 0) {
        const received = shotsOnHole(usePH, si[h]);
        net += s - received;
        gross += s;
        par_played += hp[h] ?? 0;
        thru++;
      }
    }
    entries.push({
      player_id: p.id, name: p.name, gross, handicap: usePH, net, thru, par_played,
      ...(isS36Basis ? { s36_handicap, basis_source: source, s36_net_pending: pending, allowed_entered_ph: enteredPH } : {}),
    });
  }
  // Sort by TO PAR ascending, unplayed last, S36-pending after complete
  entries.sort((a, b) => {
    if (a.s36_net_pending && !b.s36_net_pending) return 1;
    if (!a.s36_net_pending && b.s36_net_pending) return -1;
    const aU = a.par_played === 0; const bU = b.par_played === 0;
    if (aU && bU) return 0; if (aU) return 1; if (bU) return -1;
    const aTP = a.net - a.par_played; const bTP = b.net - b.par_played;
    return aTP !== bTP ? aTP - bTP : 0;
  });
  let i = 0;
  while (i < entries.length) {
    if (entries[i].par_played === 0 || entries[i].s36_net_pending) { entries[i].rank = i + 1; i++; continue; }
    let j = i;
    const iTP = entries[i].net - entries[i].par_played;
    while (j < entries.length && entries[j].par_played > 0 && !entries[j].s36_net_pending && (entries[j].net - entries[j].par_played) === iTP) j++;
    for (let k = i; k < j; k++) entries[k].rank = i + 1;
    i = j;
  }
  return entries;
}

/** Aggregated NSP league: sums per-round per-hole net totals for rounds with format='nsp'. */
export function computeNSPLeague(
  trip: Trip,
  courseForPlayer?: (round: Round, playerId: string) => Course,
  siForPlayer?: (round: Round, playerId: string) => number[],
): LeaderboardEntry[] {
  const map = new Map<string, LeaderboardEntry>();
  for (const p of trip.players) {
    map.set(p.id, { player_id: p.id, name: p.name, total: 0, per_round: {} });
  }
  const defaultSICache = new Map<string, number[]>();
  for (const round of trip.rounds) {
    if ((round as any).format !== 'nsp') continue;
    if ((round as any).scoring_mode === 'texas_scramble') continue;
    if (!defaultSICache.has(round.id)) defaultSICache.set(round.id, effectiveStrokeIndexes(round));
    const defaultSI = defaultSICache.get(round.id)!;
    const frozen = isRoundFrozen(round);
    const throughPos = frozen ? revealHoleForRound(round) : 18;
    const hp = effectiveHolePars(round);
    const basis = round.handicap_basis;
    const s36Max = round.s36_max_handicap ?? null;
    const isS36Basis = basis === 'system36' || basis === 'lower_of';
    for (const p of trip.players) {
      const strokes = round.hole_strokes?.[p.id] ?? [];
      const course = courseForPlayer ? courseForPlayer(round, p.id) : round.course;
      const enteredPH = getAllowedPH(p, course, round);
      const si = siForPlayer ? siForPlayer(round, p.id) : defaultSI;
      const gIdx = groupIndexForPlayer(round, p.id);
      const s36Hcap = isS36Basis ? s36Handicap(strokes, hp, s36Max) : null;
      const { ph: effPH } = effectivePHForBasis(basis, enteredPH, s36Hcap);
      const usePH = effPH ?? 0;
      const pending = isS36Basis && effPH == null;
      let net = 0; let thru = 0;
      for (let h = 0; h < 18; h++) {
        if (frozen && !holeInPlayingRange(round, gIdx, h, throughPos)) continue;
        const s = strokes[h];
        if (s != null && s > 0) {
          net += s - shotsOnHole(usePH, si[h]);
          thru++;
        }
      }
      if (thru === 0 || pending) continue;
      const entry = map.get(p.id)!;
      entry.per_round[round.id] = net;
      entry.total += net;
    }
  }
  const entries = Array.from(map.values()).filter(e => Object.keys(e.per_round).length > 0);
  entries.sort((a, b) => a.total - b.total); // ascending — lowest net wins
  let i = 0;
  while (i < entries.length) {
    let j = i;
    while (j < entries.length && entries[j].total === entries[i].total) j++;
    for (let k = i; k < j; k++) entries[k].rank = i + 1;
    i = j;
  }
  return entries;
}

/** Aggregated Stableford league: only individual rounds with format='stableford'. */
export function computeStablefordLeague(trip: Trip): LeaderboardEntry[] {
  const filteredTrip = {
    ...trip,
    rounds: trip.rounds.filter(r =>
      ((r as any).format || 'stableford') === 'stableford'
      && (r as any).scoring_mode !== 'texas_scramble'
    ),
  } as Trip;
  return computeIndividualLeaderboard(filteredTrip);
}

// -------- Phase 2: leaderboard freeze / reveal helpers --------

/** Valid options for the "reveal after hole N" trip setting. */
export const REVEAL_HOLE_OPTIONS = [0, 12, 13, 14, 15, 16] as const;
export const DEFAULT_REVEAL_HOLE = 15;

export function revealHoleForRound(round: Round): number {
  const v = round.reveal_hole;
  if (v != null && REVEAL_HOLE_OPTIONS.includes(v as (typeof REVEAL_HOLE_OPTIONS)[number])) {
    return v;
  }
  return DEFAULT_REVEAL_HOLE;
}

/** Cap a raw Thru count for display during a freeze.
 *  Frozen + not revealed + rawThru > freezeHole → clamp to freezeHole.
 *  Otherwise return rawThru unchanged. */
export function displayThru(rawThru: number, round: Round): number {
  if (!round || round.revealed) return rawThru;
  if (!isRoundFrozen(round)) return rawThru;
  const freezeAt = revealHoleForRound(round);
  return Math.min(rawThru, freezeAt);
}

/** Any stroke > 0 entered anywhere in the round locks the reveal-hole setting. */
export function hasAnyScoreEntered(round: Round): boolean {
  const hs = round.hole_strokes ?? {};
  for (const arr of Object.values(hs)) {
    if ((arr ?? []).some((v) => (v ?? 0) > 0)) return true;
  }
  const ths = round.team_hole_strokes ?? {};
  for (const arr of Object.values(ths)) {
    if ((arr ?? []).some((v) => (v ?? 0) > 0)) return true;
  }
  return false;
}

/** Sum of Stableford points for a player up to and including holeIdx (0-based). */
export function stablefordThroughHole(round: Round, playerId: string, holeIdx: number): number {
  const pts = round.hole_scores?.[playerId] ?? [];
  const nrHoles = round.hole_nr?.[playerId] ?? [];
  let total = 0;
  const cap = Math.min(holeIdx, pts.length - 1);
  for (let i = 0; i <= cap; i++) {
    if (nrHoles.includes(i)) continue;
    const v = pts[i];
    if (v != null && v >= 0) total += v;
  }
  return total;
}

/**
 * How far a group has progressed on the course. Returns the largest hole index
 * (0-based) where at least one player in the group has a stroke > 0. `-1` if
 * nobody has scored yet.
 */
export function groupProgressHole(round: Round, teamIdx: number): number {
  // Texas Scramble: read from team_hole_strokes instead of per-player arrays
  if (round.scoring_mode === 'texas_scramble') {
    const arr = round.team_hole_strokes?.[String(teamIdx)] ?? [];
    let furthest = -1;
    for (let h = arr.length - 1; h >= 0; h--) {
      if ((arr[h] ?? 0) > 0) { furthest = h; break; }
    }
    return furthest;
  }
  const team = round.teams[teamIdx] ?? [];
  const hs = round.hole_strokes ?? {};
  let furthest = -1;
  for (const pid of team) {
    const arr = hs[pid] ?? [];
    for (let h = arr.length - 1; h >= 0; h--) {
      if ((arr[h] ?? 0) > 0) {
        if (h > furthest) furthest = h;
        break;
      }
    }
  }
  return furthest;
}

/**
 * Has the group completed the reveal hole (all players have a stroke > 0 on
 * every hole up to and including reveal_hole - 1)? Used to gate the freeze.
 */
export function groupCompletedRevealHole(round: Round): boolean {
  const reveal = revealHoleForRound(round); // e.g. 15
  // Texas Scramble: check team_hole_strokes for each team
  if (round.scoring_mode === 'texas_scramble') {
    const ths = round.team_hole_strokes ?? {};
    for (let t = 0; t < round.teams.length; t++) {
      const arr = ths[String(t)] ?? [];
      for (let h = 0; h < reveal; h++) {
        if ((arr[h] ?? 0) === 0) return false;
      }
    }
    return true;
  }
  const hs = round.hole_strokes ?? {};
  for (const team of round.teams) {
    for (const pid of team) {
      const arr = hs[pid] ?? [];
      for (let h = 0; h < reveal; h++) {
        if ((arr[h] ?? 0) === 0) return false;
      }
    }
  }
  return true;
}

/**
 * Should the leaderboard for this round currently be FROZEN?
 * Frozen when:
 *   - reveal_hole has been completed for at least one player (i.e. scores
 *     exist on the reveal hole for anyone)
 *   - AND the round has not yet been officially "revealed"
 */
export function isRoundFrozen(round: Round): boolean {
  if (round.revealed) return false;
  // When freeze is disabled for this round, never freeze
  if (round.freeze_enabled === false) return false;
  const reveal = revealHoleForRound(round); // 1-indexed PLAYING position
  // "From the start" (reveal_hole=0): frozen as soon as freeze is enabled
  if (reveal === 0) return true;
  const hs = round.hole_strokes ?? {};
  // Freeze the moment ANY group has played past their reveal position. For a
  // standard start this is scorecard hole `reveal`. For a shotgun each group
  // has a different scorecard hole at their playing position `reveal`.
  const teams = round.teams ?? [];
  if (teams.length === 0 || !isShotgunRound(round)) {
    // Fast path: legacy standard behaviour.
    for (const arr of Object.values(hs)) {
      if ((arr?.[reveal - 1] ?? 0) > 0) return true;
    }
    return false;
  }
  for (let g = 0; g < teams.length; g++) {
    const scHole = scorecardHoleAtPlayingPos(round, g, reveal);
    for (const pid of teams[g] ?? []) {
      if ((hs[pid]?.[scHole] ?? 0) > 0) return true;
    }
  }
  return false;
}

/**
 * Are ALL groups done with 18 holes (i.e. every player has a stroke > 0 on
 * every hole)? Used as an auto-reveal condition.
 */
export function allGroupsCompleted18(round: Round): boolean {
  if (!round.teams || round.teams.length === 0) return false;
  const hs = round.hole_strokes ?? {};
  for (const team of round.teams) {
    for (const pid of team) {
      const arr = hs[pid] ?? [];
      for (let h = 0; h < 18; h++) {
        if ((arr[h] ?? 0) === 0) return false;
      }
    }
  }
  return true;
}

/**
 * How many groups have tapped "Finish Round"? Returns { finished, total }.
 * A group with no players is not counted.
 */
export function groupsFinishedSummary(round: Round): { finished: number; total: number; flags: boolean[] } {
  const total = round.teams.length;
  const flags = Array.from({ length: total }, (_, i) => Boolean(round.group_finished?.[i]));
  const finished = flags.filter(Boolean).length;
  return { finished, total, flags };
}

/**
 * Compute an individual leaderboard restricted to the first `throughHole+1`
 * playing positions per group. For standard starts (all from hole 1) this is
 * equivalent to absolute holes 0..throughHole. For multi-start rounds each
 * player's included holes are determined by their group's playing order.
 */
export function computeIndividualLeaderboardThrough(
  trip: Trip,
  currentRoundId: string,
  throughHole: number, // 0-based; for standard = abs hole cap; for multi-start = playing-position cap (1-indexed = throughHole+1)
): LeaderboardEntry[] {
  const map = new Map<string, LeaderboardEntry>();
  for (const p of trip.players) {
    map.set(p.id, { player_id: p.id, name: p.name, total: 0, per_round: {} });
  }
  const throughPos = throughHole + 1; // 1-indexed playing position cap
  for (const round of trip.rounds) {
    const isCurrent = round.id === currentRoundId;
    for (const p of trip.players) {
      const entry = map.get(p.id);
      if (!entry) continue;
      let sf = 0;
      if (isCurrent) {
        const gIdx = groupIndexForPlayer(round, p.id);
        const nrHoles = round.hole_nr?.[p.id] ?? [];
        if (isMultiStartRound(round) && gIdx >= 0) {
          // Sum only holes within the player's first throughPos playing positions
          const pts = round.hole_scores?.[p.id] ?? [];
          for (let h = 0; h < 18; h++) {
            if (nrHoles.includes(h)) continue;
            if (holeInPlayingRange(round, gIdx, h, throughPos)) {
              const v = pts[h];
              if (v != null && v >= 0) sf += v;
            }
          }
        } else {
          sf = stablefordThroughHole(round, p.id, throughHole);
        }
      } else {
        sf = stablefordThroughHole(round, p.id, 17);
      }
      if (sf > 0) {
        entry.per_round[round.id] = sf;
        entry.total += sf;
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

/**
 * Team round points computed only through hole `throughHole` (0-based, inclusive).
 * Uses the same "top-N on par 4/5, all on par 3" rule as `teamRoundPoints`,
 * with N scaled to the group size via `countingScoresOnLongHoles`.
 */
export function teamRoundPointsThrough(
  teams: string[][],
  hole_scores: Record<string, number[]> | undefined,
  hole_pars: number[] | undefined,
  throughHole: number,
  round?: Round,
): { team_index: number; player_ids: string[]; stableford_total: number; rank: number }[] {
  const throughPos = throughHole + 1;
  const hole_nr = round?.hole_nr;
  const withScore = teams
    .map((players, idx) => {
      let total = 0;
      if (hole_scores && hole_pars && hole_pars.length === 18 && throughHole >= 0) {
        for (let h = 0; h < 18; h++) {
          if (round && isMultiStartRound(round)) {
            if (!holeInPlayingRange(round, idx, h, throughPos)) continue;
          } else {
            if (h > throughHole) continue;
          }
          const par = hole_pars[h] ?? 4;
          const countN = countForPar(round, par, players.length);
          const pts = players
            .map((pid) => {
              if (hole_nr?.[pid]?.includes(h)) return undefined;
              return hole_scores[pid]?.[h];
            })
            .filter((v): v is number => Number.isFinite(v) && (v as number) >= 0);
          if (pts.length === 0) continue;
          pts.sort((a, b) => b - a);
          const take = Math.min(countN, pts.length);
          for (let k = 0; k < take; k++) total += pts[k] ?? 0;
        }
      }
      return { team_index: idx, player_ids: players, stableford_total: total };
    });

  const sorted = [...withScore].sort((a, b) => b.stableford_total - a.stableford_total);
  const ranked: { team_index: number; player_ids: string[]; stableford_total: number; rank: number }[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j < sorted.length && sorted[j].stableford_total === sorted[i].stableford_total) j++;
    for (let k = i; k < j; k++) {
      ranked.push({ ...sorted[k], rank: i + 1 });
    }
    i = j;
  }
  return ranked;
}

// Format a decimal as a whole-or-fractional string.
export function fmtPts(n: number): string {
  if (n === 0) return "0";
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(1);
}

// -------- Tee times (per round, per group) --------

export const TEE_GAP_OPTIONS = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15] as const;
export const DEFAULT_TEE_GAP = 8;

/** Parse "HH:MM" into minutes-since-midnight. Returns null if malformed. */
function parseHHMM(s: string | null | undefined): number | null {
  if (!s) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(h) || !Number.isFinite(mm)) return null;
  if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
  return h * 60 + mm;
}

function formatHHMM(minutes: number): string {
  const h = Math.floor(((minutes % (24 * 60)) + 24 * 60) % (24 * 60) / 60);
  const m = ((minutes % 60) + 60) % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Tee time for a specific group in a round, or `null` when no first tee
 *  time is set. Individual group overrides win; otherwise it's
 *  `first_tee_time + groupIndex * gap`. If no first tee time is configured
 *  the ENTIRE feature is off — per-group overrides are ignored so the app
 *  shows no tee times anywhere (per product spec). */
export function teeTimeForGroup(round: Round, groupIndex: number): string | null {
  const start = parseHHMM(round.first_tee_time ?? null);
  if (start == null && !isSplitTeeRound(round)) return null;
  // Shotgun / two-tee shotgun: every group tees off simultaneously.
  if (isShotgunRound(round)) {
    if (start == null) return null;
    return formatHHMM(start);
  }
  // Split-tee: each cluster has its own first-tee-time; groups within a
  // cluster are staggered by tee_gap_minutes.
  if (isSplitTeeRound(round)) {
    const holes = round.split_tee_holes ?? [1, 10];
    const times = (round as any).split_tee_first_times as (string | null)[] | undefined ?? [];
    const groupHole = startHoleForGroup(round, groupIndex);
    const clusterIdx = holes.indexOf(groupHole);
    const clusterTime = parseHHMM(clusterIdx >= 0 ? (times[clusterIdx] ?? null) : null);
    if (clusterTime == null) return null;
    // Count groups before this one in the same cluster
    let posInCluster = 0;
    for (let g = 0; g < groupIndex; g++) {
      if (startHoleForGroup(round, g) === groupHole) posInCluster++;
    }
    const gap = round.tee_gap_minutes ?? DEFAULT_TEE_GAP;
    return formatHHMM(clusterTime + posInCluster * gap);
  }
  // Standard sequential
  if (start == null) return null;
  const override = round.group_tee_times?.[groupIndex];
  if (override && parseHHMM(override) != null) return override;
  const gap = round.tee_gap_minutes ?? DEFAULT_TEE_GAP;
  return formatHHMM(start + groupIndex * gap);
}

/** Whether the round has ANY tee-time information — only the first tee time
 *  matters. Group overrides alone don't count. */
export function roundHasTeeTimes(round: Round): boolean {
  return parseHHMM(round.first_tee_time ?? null) != null;
}

// -------- Shotgun start --------

export const MAX_SHOTGUN_GROUPS = 18;

/** Physical hole (1-based) at a given sequence position (1-based) given a starting hole.
 *  Single source of truth for hole-sequence math.
 *  physicalHoleAt(12, 18, 1) → 12, physicalHoleAt(12, 18, 7) → 18, physicalHoleAt(12, 18, 8) → 1 */
export function physicalHoleAt(startingHole: number, totalHoles: number, sequencePosition: number): number {
  return ((startingHole - 1 + sequencePosition - 1) % totalHoles) + 1;
}

export function isShotgunRound(round: Round): boolean {
  return round.start_type === "shotgun" || round.start_type === "two_tee_shotgun";
}

export function isSplitTeeRound(round: Round): boolean {
  return round.start_type === "split_tee";
}

/** True when groups may start on different holes (shotgun OR split-tee). */
export function isMultiStartRound(round: Round): boolean {
  return isShotgunRound(round) || isSplitTeeRound(round);
}

/** Starting hole (1..18) for a given group. Standard rounds always return 1.
 *  Manual overrides in `round.group_start_holes` win; otherwise defaults:
 *    - full shotgun: group N → hole N+1
 *    - two-tee shotgun: even indexes → hole 1, odd → hole 12 (Team 1 & 3 & 5…
 *      on hole 1; Team 2 & 4 & 6… on hole 12). */
export function startHoleForGroup(round: Round, groupIndex: number): number {
  // Manual override always wins for any multi-start type
  const override = round.group_start_holes?.[groupIndex];
  if (typeof override === "number" && override >= 1 && override <= 18) return override;
  if (isSplitTeeRound(round)) {
    // Default alternating between the two split-tee holes
    const holes = round.split_tee_holes ?? [1, 10];
    return holes[groupIndex % holes.length] ?? 1;
  }
  if (!isShotgunRound(round)) return round.starting_hole || 1;
  if (round.start_type === "two_tee_shotgun") {
    return groupIndex % 2 === 0 ? 1 : 12;
  }
  // Full shotgun: sequential from hole 1
  return (groupIndex % 18) + 1;
}

/** Returns an 18-length array of 0-indexed scorecard hole indexes in the
 *  order the specified group plays them. */
export function playingOrderForGroup(round: Round, groupIndex: number): number[] {
  const start0 = startHoleForGroup(round, groupIndex) - 1;
  return Array.from({ length: 18 }, (_, i) => (start0 + i) % 18);
}

/** 0-indexed scorecard hole a group is playing at their 1-indexed
 *  `playingPos`. Position 1 = first hole they play. */
export function scorecardHoleAtPlayingPos(
  round: Round,
  groupIndex: number,
  playingPos1Based: number,
): number {
  const order = playingOrderForGroup(round, groupIndex);
  const clamped = Math.max(1, Math.min(18, playingPos1Based));
  return order[clamped - 1];
}

/** Given a 0-indexed scorecard hole, what playing position (1..18) is this
 *  for the given group? */
export function playingPosForScorecardHole(
  round: Round,
  groupIndex: number,
  scorecardHole0: number,
): number {
  const order = playingOrderForGroup(round, groupIndex);
  const pos = order.indexOf(scorecardHole0);
  return pos < 0 ? 1 : pos + 1;
}

export function ordinalSuffix(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return "th";
  const mod10 = n % 10;
  return mod10 === 1 ? "st" : mod10 === 2 ? "nd" : mod10 === 3 ? "rd" : "th";
}

/** Find the team/group index a player belongs to. Returns -1 if not found. */
export function groupIndexForPlayer(round: Round, playerId: string): number {
  for (let g = 0; g < (round.teams ?? []).length; g++) {
    if ((round.teams[g] ?? []).includes(playerId)) return g;
  }
  return -1;
}

/** Should absolute scorecard hole `h` (0-based) be included when summing
 *  up to playing-position `throughPos` (1-indexed) for the given group?
 *  For standard starts this reduces to `h < throughPos`.
 *  For multi-start rounds it checks the group's actual playing order. */
export function holeInPlayingRange(round: Round, groupIdx: number, h: number, throughPos: number): boolean {
  if (groupIdx < 0 && !isMultiStartRound(round) && (round.starting_hole || 1) === 1) return h < throughPos;
  // For any non-default-start or multi-start round, check via playing order
  const effectiveGroup = groupIdx >= 0 ? groupIdx : 0;
  return playingPosForScorecardHole(round, effectiveGroup, h) <= throughPos;
}


// -------- Trip Teams (persistent cross-round team standings) --------

export interface TripTeamMember {
  player_id: string;
  name: string;
  stableford: number;
  nsp: number;
}

export interface TripTeamStanding {
  team_id: string;
  team_name: string;
  color: string;
  player_ids: string[];
  member_count: number;
  members: TripTeamMember[];
  stableford_total: number;
  nsp_total: number;
  stableford_rounds: number;
  nsp_rounds: number;
}

/** Compute aggregated standings for persistent trip teams across all rounds.
 *  Stableford and NSP totals are computed separately (never combined).
 *  Freeze-through-hole is respected via isRoundFrozen / revealHoleForRound. */
export function computeTripTeamStandings(
  trip: Trip,
  courseForPlayer?: (round: Round, playerId: string) => Course,
  siForPlayer?: (round: Round, playerId: string) => number[],
): TripTeamStanding[] {
  const tripTeams = trip.trip_teams ?? [];
  if (tripTeams.length === 0) return [];

  return tripTeams.map(team => {
    let sfTotal = 0;
    let nspTotal = 0;
    const sfRounds = new Set<string>();
    const nspRounds = new Set<string>();
    // Per-player accumulators
    const playerSf = new Map<string, number>();
    const playerNsp = new Map<string, number>();
    for (const pid of team.player_ids) { playerSf.set(pid, 0); playerNsp.set(pid, 0); }

    for (const round of trip.rounds) {
      const fmt = round.format || 'stableford';
      const frozen = isRoundFrozen(round);
      const throughPos = frozen ? revealHoleForRound(round) : 18;

      if (fmt === 'stableford') {
        let contrib = 0;
        let scored = false;
        for (const pid of team.player_ids) {
          let sf = 0;
          const nrHoles = round.hole_nr?.[pid] ?? [];
          if (frozen) {
            const gIdx = groupIndexForPlayer(round, pid);
            const pts = round.hole_scores?.[pid] ?? [];
            for (let h = 0; h < 18; h++) {
              if (nrHoles.includes(h)) continue;
              if (holeInPlayingRange(round, gIdx, h, throughPos)) {
                const v = pts[h];
                if (v != null && v >= 0) sf += v;
              }
            }
          } else {
            sf = stablefordThroughHole(round, pid, 17);
          }
          if (sf > 0) { contrib += sf; scored = true; playerSf.set(pid, (playerSf.get(pid) ?? 0) + sf); }
        }
        if (scored) { sfTotal += contrib; sfRounds.add(round.id); }
      } else if (fmt === 'nsp') {
        let contrib = 0;
        let scored = false;
        const dsi = effectiveStrokeIndexes(round);
        for (const pid of team.player_ids) {
          const player = trip.players.find(p => p.id === pid);
          if (!player) continue;
          const strokes = round.hole_strokes?.[pid] ?? [];
          const course = courseForPlayer ? courseForPlayer(round, pid) : round.course;
          const ph = getAllowedPH(player, course, round);
          const si = siForPlayer ? siForPlayer(round, pid) : dsi;
          const gIdx = groupIndexForPlayer(round, pid);
          let net = 0;
          let thru = 0;
          for (let h = 0; h < 18; h++) {
            if (frozen && !holeInPlayingRange(round, gIdx, h, throughPos)) continue;
            const s = strokes[h];
            if (s != null && s > 0) { net += s - shotsOnHole(ph, si[h]); thru++; }
          }
          if (thru > 0) { contrib += net; scored = true; playerNsp.set(pid, (playerNsp.get(pid) ?? 0) + net); }
        }
        if (scored) { nspTotal += contrib; nspRounds.add(round.id); }
      }
    }

    const members: TripTeamMember[] = team.player_ids.map(pid => {
      const p = trip.players.find(pl => pl.id === pid);
      return { player_id: pid, name: p?.name ?? pid, stableford: playerSf.get(pid) ?? 0, nsp: playerNsp.get(pid) ?? 0 };
    });

    return {
      team_id: team.id,
      team_name: team.name,
      color: team.color,
      player_ids: [...team.player_ids],
      member_count: team.player_ids.length,
      members,
      stableford_total: sfTotal,
      nsp_total: nspTotal,
      stableford_rounds: sfRounds.size,
      nsp_rounds: nspRounds.size,
    };
  });
}


// -------- Final Round: standings-seeded draw --------

/** Compute a standings-seeded draw for a Final Round.
 *  Players are ranked by cumulative standings in the round's own format across
 *  all prior revealed format-matching rounds. Leaders land in the LAST group
 *  (latest tee time). First group gets the fewest players when non-divisible. */
export function computeFinalRoundSeededDraw(
  trip: Trip,
  finalRound: Round,
  priorRevealedRounds: Round[],
  teamSize: number,
): string[][] {
  const activePlayers = trip.players.filter(p => p.active !== false);
  if (priorRevealedRounds.length === 0 || activePlayers.length === 0) return [];

  const format = finalRound.format || "stableford";

  // Build a synthetic trip scoped to only the prior revealed rounds
  const syntheticTrip = { ...trip, rounds: priorRevealedRounds } as Trip;

  // Rank via existing helpers (includes countback for stableford)
  const rankedEntries =
    format === "nsp"
      ? computeNSPLeague(syntheticTrip)
      : computeIndividualLeaderboard(syntheticTrip);

  // Ranked IDs in standings order (best first)
  const rankedIds = rankedEntries.map((e) => e.player_id);

  // Append active players with no prior scores at the end
  const inRanked = new Set(rankedIds);
  for (const p of activePlayers) {
    if (!inRanked.has(p.id)) rankedIds.push(p.id);
  }

  // Fill groups from end backward: leaders in last group
  const numGroups = Math.max(1, Math.ceil(rankedIds.length / teamSize));
  const groups: string[][] = Array.from({ length: numGroups }, () => []);

  let idx = 0;
  for (let g = numGroups - 1; g >= 0; g--) {
    const size =
      g === 0
        ? rankedIds.length - (numGroups - 1) * teamSize
        : teamSize;
    for (let i = 0; i < size && idx < rankedIds.length; i++) {
      groups[g].push(rankedIds[idx++]);
    }
  }

  return groups;
}
